import type { CatalogSort, ListPiecesParams } from "@/lib/api/api-client";

/**
 * Forme d'URL de la bibliothèque, et traduction entre la barre d'adresse et
 * l'état de parcours.
 *
 * # Pourquoi `/library` et pas `/studio/library`
 *
 * `app/(main)/layout.tsx` ne rend `children` que **hors** des routes studio
 * (`{!isStudio && children}`) et recouvre entièrement ces routes d'un calque
 * « tourne ton appareil » en portrait sur téléphone. Une vue `/studio/*`
 * n'aurait donc rien rendu du tout, et aurait été masquée précisément sur
 * l'appareil et l'orientation où quelqu'un découvre le produit. `/library` est
 * une sœur de `/learn` : elle passe par `children`, et `StudioPage` reste
 * monté-mais-caché — aucun remontage du piano-roll, aucun rechargement des
 * samples.
 *
 * # L'état de parcours vit dans l'URL
 *
 * Recherche, genre et tri sont des paramètres de requête. Le
 * retour navigateur et le partage de lien fonctionnent alors sans une ligne de
 * code dédiée, et il n'y a pas d'état de navigation hors URL à resynchroniser
 * — c'était le défaut du `libraryOpen` de la modale.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/library-routes.test.ts`.
 */

/** Lecture minimale de `URLSearchParams` (compatible avec `ReadonlyURLSearchParams`). */
type SearchParamsLike = { get(name: string): string | null };

export const LIBRARY_BASE_PATH = "/library";
export const LIBRARY_MINE_PATH = "/library/mine";

/**
 * Les vues de « Ma bibliothèque » sont des chemins, pas des `?filter=`.
 *
 * Chacune répond à une question différente — « où en suis-je », « qu'est-ce
 * qu'on m'a envoyé », « qu'est-ce que j'ai rangé » — et une question qui a sa
 * réponse a une page. Un paramètre les rendrait aussi indistinguables dans un
 * historique de navigation que dans une barre d'adresse partagée.
 *
 * Ces segments sont **réservés** : `/library/[slug]` sert les fiches, et Next
 * fait gagner le segment statique. Un morceau dont le slug vaudrait `resume`
 * serait donc inatteignable — improbable, les slugs étant de la forme
 * `satie-gymnopedie-1`, mais c'est le prix de la forme d'URL.
 */
export const LIBRARY_RESUME_PATH = "/library/resume";
export const LIBRARY_SHARED_PATH = "/library/shared";
export const LIBRARY_PLAYLISTS_PATH = "/library/playlists";

/** L'atterrissage d'un lien de partage. Deux segments : pas de collision. */
export const LIBRARY_SHARE_PATH = "/library/share";

/**
 * Les vues de « Mes morceaux » qui n'ont pas mérité leur propre page.
 *
 * « Mes imports » n'est pas une question qu'on se pose en arrivant : c'est un
 * tri qu'on applique une fois sur place, pour retrouver un fichier qu'on a
 * déposé. Il reste donc un paramètre de la page, là où « en cours », « partagé
 * avec moi » et « mes playlists » sont des destinations.
 */
export type LibraryMineFilter = "all" | "imports";

/** L'adresse de « Mes morceaux », éventuellement restreinte aux imports. */
export function libraryMinePath(filter: LibraryMineFilter = "all"): string {
  return filter === "all" ? LIBRARY_MINE_PATH : `${LIBRARY_MINE_PATH}?filter=imports`;
}

/** Ce que l'URL de « Mes morceaux » demande. Toute autre valeur retombe sur tout. */
export function parseMineFilter(params: SearchParamsLike): LibraryMineFilter {
  return params.get("filter") === "imports" ? "imports" : "all";
}

/**
 * Vrai sur l'atterrissage d'un lien de partage.
 *
 * La coque de la bibliothèque s'en sert pour ne **pas** monter la carte du
 * répertoire : quelqu'un qui ouvre un lien n'a pas de bibliothèque à parcourir,
 * et lui en montrer une avant qu'il ait vu le morceau reviendrait à lui
 * demander de choisir alors qu'on vient de lui envoyer quelque chose de précis.
 */
export function isLibraryShareLandingPathname(pathname: string): boolean {
  return pathname.startsWith(`${LIBRARY_SHARE_PATH}/`);
}

/**
 * Les sélections ont leur propre chemin, elles ne sont plus un paramètre.
 *
 * « Sélection » plutôt que « rayon » : un rayon range un catalogue de façon
 * exhaustive et chaque livre n'est que dans un seul. Ici les listes se
 * recoupent, elles sont arrêtées à la main et choisies par intention — ce que
 * « sélection » dit, et qui est en soi un argument : quelqu'un a choisi.
 *
 * Et un chemin plutôt qu'un `?shelf=` parce qu'une liste éditoriale est une
 * page à part entière : « Court, pour ce soir » répond à une question qu'on
 * pose vraiment, et un paramètre de requête rendu en client n'est indexable
 * par personne. La recherche, elle, reste un paramètre : basculer de route à
 * chaque frappe démonterait le champ.
 */
export const LIBRARY_SELECTION_PATH = "/library/selection";

/** Le tri appliqué quand l'URL n'en demande pas : celui de la découverte. */
export const DEFAULT_CATALOG_SORT: CatalogSort = "playability";

const SORT_VALUES: readonly CatalogSort[] = [
  "popularity",
  "playability",
  "duration",
  "title",
  "recent",
];

/**
 * Ce que l'URL dit du parcours en cours : la recherche libre, un genre, un
 * tri. Une sélection n'en fait plus partie — elle a sa propre page, qui rejoue
 * les critères que le serveur lui donne sans qu'aucun n'atterrisse dans la
 * barre d'adresse. Les recopier dupliquerait la définition d'une sélection des
 * deux côtés, et un lien partagé appliquerait l'ancienne le jour où le serveur
 * la change.
 */
export type LibraryBrowseState = {
  /** Texte de recherche, déjà nettoyé. Vide = pas de recherche. */
  readonly query: string;
  /** Genre actif. Le catalogue étant à 97 % classique, il n'a pas de surface
   *  dédiée — mais un lien profond doit continuer de fonctionner. */
  readonly genre: string | null;
  readonly sort: CatalogSort;
  /**
   * `?drafts=1` fait entrer les pièces non publiées dans les listes. Ouvert à
   * tous parce qu'il ne décide de rien : l'API ne l'honore que pour un admin.
   * Conservé d'une URL à l'autre, sans quoi un aller-retour vers une fiche
   * ferait retomber la session de relecture sur le catalogue public.
   */
  readonly drafts: boolean;
};

export const EMPTY_BROWSE_STATE: LibraryBrowseState = {
  query: "",
  genre: null,
  sort: DEFAULT_CATALOG_SORT,
  drafts: false,
};

export function parseBrowseState(
  params: SearchParamsLike,
): LibraryBrowseState {
  const rawSort = params.get("sort");

  return {
    query: (params.get("q") ?? "").trim(),
    genre: emptyToNull(params.get("genre")),
    sort: isCatalogSort(rawSort) ? rawSort : DEFAULT_CATALOG_SORT,
    drafts: params.get("drafts") === "1",
  };
}

/**
 * Vrai quand l'URL demande une **liste** plutôt que l'accueil en sélections.
 *
 * Le tri seul ne suffit pas à basculer : `?sort=playability` est le défaut, et
 * un lien qui le porte explicitement doit rendre le même accueil qu'un lien
 * qui l'omet.
 */
export function isBrowsing(state: LibraryBrowseState): boolean {
  return (
    state.query !== "" ||
    state.genre !== null ||
    state.sort !== DEFAULT_CATALOG_SORT
  );
}

/**
 * URL de la bibliothèque pour un état donné. Les valeurs par défaut sont
 * omises : l'accueil reste `/library` tout court, et les liens partagés ne
 * transportent que ce qui a été réellement choisi.
 */
export function libraryBrowsePath(state: LibraryBrowseState): string {
  const params = new URLSearchParams();

  if (state.query !== "") {
    params.set("q", state.query);
  }

  if (state.genre !== null) {
    params.set("genre", state.genre);
  }

  if (state.sort !== DEFAULT_CATALOG_SORT) {
    params.set("sort", state.sort);
  }

  if (state.drafts) {
    params.set("drafts", "1");
  }

  const search = params.toString();

  return search === "" ? LIBRARY_BASE_PATH : `${LIBRARY_BASE_PATH}?${search}`;
}

/** L'accueil, en conservant le mode relecture s'il est actif. */
export function libraryHomePath(drafts: boolean): string {
  return libraryBrowsePath({ ...EMPTY_BROWSE_STATE, drafts });
}

/**
 * « Parcourir les N morceaux » : tout le répertoire, sans critère.
 *
 * Trié par titre, et pas par jouabilité : cette entrée est la sortie de
 * secours de qui sait déjà ce qu'il cherche — l'ordre alphabétique est le seul
 * dans lequel on peut *viser* une ligne. C'est aussi ce qui distingue l'URL de
 * celle de l'accueil, dont le tri est le défaut.
 */
export function libraryBrowseAllPath(drafts: boolean): string {
  return libraryBrowsePath({ ...EMPTY_BROWSE_STATE, drafts, sort: "title" });
}

/** La sélection `key`, sur sa propre page. */
export function librarySelectionPath(key: string, drafts: boolean): string {
  const path = `${LIBRARY_SELECTION_PATH}/${encodeURIComponent(key)}`;

  return drafts ? `${path}?drafts=1` : path;
}

/**
 * L'adresse d'une fiche : le slug, l'identifiant seulement à défaut.
 *
 * Le slug est le nom du morceau, donc l'adresse dit ce qu'elle sert —
 * `/library/satie-gymnopedie-1` plutôt qu'un UUID. Le repli existe pour les
 * pièces qu'`app:catalog:slugs` n'a pas encore vues : mieux vaut une adresse
 * laide qu'un lien mort, et la fiche redirige elle-même vers la forme
 * canonique dès que le slug arrive.
 */
export function libraryPiecePath(piece: { id: string; slug?: string | null }): string {
  return `${LIBRARY_BASE_PATH}/${encodeURIComponent(piece.slug ?? piece.id)}`;
}

/** Le contenu d'une playlist. */
export function libraryPlaylistPath(playlistId: string): string {
  return `${LIBRARY_PLAYLISTS_PATH}/${encodeURIComponent(playlistId)}`;
}

/** L'atterrissage d'un lien de partage, côté application. */
export function libraryShareLandingPath(token: string): string {
  return `${LIBRARY_SHARE_PATH}/${encodeURIComponent(token)}`;
}

/**
 * L'adresse complète à copier pour partager un morceau.
 *
 * L'API ne rend qu'un jeton, jamais d'URL : elle ne sait pas sous quel nom de
 * domaine le front est servi (virtuosa.io, une préproduction, un `localhost`,
 * une coque Capacitor). L'origine entre donc par paramètre — c'est aussi ce qui
 * garde ce module pur, sans lire `window`.
 */
export function libraryShareUrl(origin: string, token: string): string {
  return `${origin.replace(/\/$/, "")}${libraryShareLandingPath(token)}`;
}

/** Vrai quand la bibliothèque occupe le viewport sur ce chemin. */
export function isLibraryPathname(pathname: string): boolean {
  return (
    pathname === LIBRARY_BASE_PATH || pathname.startsWith(`${LIBRARY_BASE_PATH}/`)
  );
}

/**
 * Les paramètres d'API d'un état de parcours.
 *
 * Une sélection ne passe pas par ici : ses critères sont `CatalogShelf.query`,
 * que sa page rejoue tels quels. Cette fonction ne traduit que ce que l'URL
 * porte elle-même.
 */
export function browseListParams(
  state: LibraryBrowseState,
): ListPiecesParams {
  return {
    search: state.query === "" ? undefined : state.query,
    genre: state.genre ?? undefined,
    sort: state.sort,
    drafts: state.drafts,
  };
}

function emptyToNull(value: string | null): string | null {
  if (value === null || value.trim() === "") {
    return null;
  }

  return value;
}

function isCatalogSort(value: string | null): value is CatalogSort {
  return value !== null && (SORT_VALUES as readonly string[]).includes(value);
}
